/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * `connected`: the user is signed in. `not_connected`: the user has not signed in. `reauthorization_required`: a sign-in exists but must be renewed. `not_required`: the server needs no sign-in from the user. `setup_required`: an administrator must finish the server's sign-in setup first. `no_access`: the user lacks permission to connect to the server. `unknown`: the state can't be determined, for example for a gateway.
 */
export const AgentServerConnectionState = {
  Connected: "connected",
  NotConnected: "not_connected",
  ReauthorizationRequired: "reauthorization_required",
  NotRequired: "not_required",
  SetupRequired: "setup_required",
  NoAccess: "no_access",
  Unknown: "unknown",
} as const;
/**
 * `connected`: the user is signed in. `not_connected`: the user has not signed in. `reauthorization_required`: a sign-in exists but must be renewed. `not_required`: the server needs no sign-in from the user. `setup_required`: an administrator must finish the server's sign-in setup first. `no_access`: the user lacks permission to connect to the server. `unknown`: the state can't be determined, for example for a gateway.
 */
export type AgentServerConnectionState = OpenEnum<
  typeof AgentServerConnectionState
>;

export type AgentServerConnection = {
  /**
   * Page that signs the user in to this server in the browser. Present when state is not_connected or reauthorization_required.
   */
  connectUrl?: string | undefined;
  /**
   * True when the user hid this server, from the gateway consent page or the device agent. Absent when not hidden.
   */
  hidden?: boolean | undefined;
  /**
   * The server's id, as getPlugins reports it on each plugin server.
   */
  id: string;
  /**
   * Page where the user can ask an administrator for access to this server. Present only when state is no_access and the organization accepts access requests.
   */
  requestAccessUrl?: string | undefined;
  /**
   * `connected`: the user is signed in. `not_connected`: the user has not signed in. `reauthorization_required`: a sign-in exists but must be renewed. `not_required`: the server needs no sign-in from the user. `setup_required`: an administrator must finish the server's sign-in setup first. `no_access`: the user lacks permission to connect to the server. `unknown`: the state can't be determined, for example for a gateway.
   */
  state: AgentServerConnectionState;
};

/** @internal */
export const AgentServerConnectionState$inboundSchema: z.ZodMiniType<
  AgentServerConnectionState,
  unknown
> = openEnums.inboundSchema(AgentServerConnectionState);

/** @internal */
export const AgentServerConnection$inboundSchema: z.ZodMiniType<
  AgentServerConnection,
  unknown
> = z.pipe(
  z.object({
    connect_url: z.optional(z.string()),
    hidden: z.optional(z.boolean()),
    id: z.string(),
    request_access_url: z.optional(z.string()),
    state: AgentServerConnectionState$inboundSchema,
  }),
  z.transform((v) => {
    return remap$(v, {
      "connect_url": "connectUrl",
      "request_access_url": "requestAccessUrl",
    });
  }),
);

export function agentServerConnectionFromJSON(
  jsonString: string,
): SafeParseResult<AgentServerConnection, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AgentServerConnection$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AgentServerConnection' from JSON`,
  );
}
